import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusUI";
import { fmtDate } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  TrendingUp, Scissors, PackageX, Target, Package, AlertOctagon, Loader2, ChevronRight, ChevronDown, Wifi, WifiOff,
} from "lucide-react";

const KPIS = {
  taxa_atendimento: { title: "Taxa de Atendimento", icon: TrendingUp, suffix: "%", color: "text-emerald-300", ring: "border-emerald-600/50" },
  indice_corte: { title: "Índice de Corte", icon: Scissors, suffix: "%", color: "text-red-300", ring: "border-red-600/50" },
  indice_avaria: { title: "Índice de Avaria", icon: PackageX, suffix: "%", color: "text-rose-300", ring: "border-rose-600/50" },
  acuracidade_inventario: { title: "Acuracidade Inventário", icon: Target, suffix: "%", color: "text-blue-300", ring: "border-blue-600/50" },
  pedidos_ativos: { title: "Pedidos Ativos", icon: Package, suffix: "", color: "text-primary", ring: "border-primary/50" },
  estoque_critico: { title: "Estoque Crítico (SKUs)", icon: AlertOctagon, suffix: "", color: "text-amber-300", ring: "border-amber-600/50" },
};
const PRIMARY_KEYS = ["taxa_atendimento", "pedidos_ativos", "indice_corte", "estoque_critico"];
const SECONDARY_KEYS = ["indice_avaria", "acuracidade_inventario"];
const ALL_KEYS = ["taxa_atendimento", "indice_corte", "indice_avaria", "acuracidade_inventario", "pedidos_ativos", "estoque_critico"];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [showMoreKpis, setShowMoreKpis] = useState(false);
  const [conn, setConn] = useState("connecting"); // connecting | live | reconnecting
  const [unitSel, setUnitSel] = useState("__todas");
  const revRef = useRef(null);
  const unitRef = useRef("");
  const nav = useNavigate();

  const unitSuffix = (sep) => (unitRef.current ? `${sep}unit_id=${unitRef.current}` : "");
  const kpiTarget = (key) => {
    switch (key) {
      case "estoque_critico": return `/estoque?situacao=critico${unitSuffix("&")}`;
      case "indice_avaria": return "/ocorrencias?tipo=AVARIA";
      case "indice_corte": return "/ocorrencias?tipo=CORTE";
      case "acuracidade_inventario": return "/inventarios?status=FINALIZADO";
      case "pedidos_ativos": return `/pedidos?ativos=1${unitSuffix("&")}`;
      case "taxa_atendimento": return `/pedidos${unitSuffix("?")}`;
      default: return "/pedidos";
    }
  };

  const loadDashboard = useCallback(async () => {
    const qs = unitRef.current ? `?unit_id=${unitRef.current}` : "";
    const r = await api.get("/dashboard" + qs);
    setData(r.data);
  }, []);

  const tick = useCallback(async () => {
    try {
      const qs = unitRef.current ? `?unit_id=${unitRef.current}` : "";
      const { data: pulse } = await api.get("/dashboard/pulse" + qs);
      setConn("live");
      if (pulse.rev !== revRef.current) {
        revRef.current = pulse.rev;
        await loadDashboard();
      }
    } catch (_) {
      setConn("reconnecting");
    }
  }, [loadDashboard]);

  const onUnitChange = (v) => {
    setUnitSel(v);
    unitRef.current = v === "__todas" ? "" : v;
    revRef.current = null;
    loadDashboard();
  };

  useEffect(() => {
    let alive = true;
    (async () => { try { await loadDashboard(); if (alive) await tick(); } catch (_) { setConn("reconnecting"); } })();
    const iv = setInterval(tick, 7000);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [loadDashboard, tick]);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const KpiCard = ({ kkey }) => {
    const k = KPIS[kkey];
    const Icon = k.icon;
    const raw = data.kpis[kkey];
    const empty = raw === null || raw === undefined;
    return (
      <Card onClick={() => nav(kpiTarget(kkey))} data-testid={`kpi-${kkey}`}
        className={`bg-card/70 border ${k.ring} p-3 sm:p-4 cursor-pointer hover:bg-card transition-colors group`}>
        <div className="flex items-start justify-between gap-2">
          <div className="text-[11px] sm:text-xs text-muted-foreground uppercase tracking-wide leading-tight">{k.title}</div>
          <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${k.color} opacity-70 group-hover:opacity-100 shrink-0`} />
        </div>
        {empty ? (
          <div className="mt-1.5 sm:mt-2" data-testid={`kpi-${kkey}-empty`}>
            <div className="font-display text-2xl sm:text-4xl font-extrabold text-muted-foreground/50">—</div>
            <div className="text-[10px] sm:text-xs text-muted-foreground/70 -mt-0.5">Sem dados</div>
          </div>
        ) : (
          <div className={`font-display text-2xl sm:text-4xl font-extrabold mt-1.5 sm:mt-2 ${k.color}`}>
            {raw}<span className="text-base sm:text-lg">{k.suffix}</span>
          </div>
        )}
      </Card>
    );
  };

  const fluxoOrdenado = [...data.fluxo].sort((a, b) => (b.count > 0) - (a.count > 0));

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="dashboard">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] sm:text-xs text-primary uppercase tracking-[0.18em] font-bold">
            {data.escopo === "EMPRESA" ? "Painel Geral da Empresa" : data.escopo === "MULTI" ? "Painel por Unidade" : "Painel da Unidade"}
          </div>
          <h1 className="font-display text-2xl sm:text-4xl font-extrabold uppercase" data-testid="dash-title">
            {(() => {
              const cur = data.unidade_atual && (data.unidades || []).find((u) => u.id === data.unidade_atual);
              if (cur) return cur.nome;
              if (data.escopo === "UNIDADE" && data.unidades?.[0]) return data.unidades[0].nome;
              return "Visão Geral";
            })()}
          </h1>
        </div>
        <div className="flex items-center gap-2">
        {data.escopo === "MULTI" && (
          <Select value={unitSel} onValueChange={onUnitChange}>
            <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="dash-unit-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__todas">Todas as minhas unidades</SelectItem>
              {(data.unidades || []).map((u) => <SelectItem key={u.id} value={u.id} data-testid={`dash-unit-${u.codigo}`}>{u.codigo} — {u.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-mono ${conn === "live" ? "border-emerald-600/50 text-emerald-300 bg-emerald-950/40" : "border-amber-600/50 text-amber-300 bg-amber-950/40"}`} data-testid="realtime-indicator">
          {conn === "live" ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5 animate-pulse" />}
          {conn === "live" ? "Tempo real conectado" : "Reconectando..."}
        </div>
        </div>
      </div>

      {/* KPIs — mobile: 2x2 principais + ver mais / desktop: 3 col */}
      <div className="lg:hidden space-y-3">
        <div className="grid grid-cols-2 gap-3">
          {PRIMARY_KEYS.map((k) => <KpiCard key={k} kkey={k} />)}
        </div>
        {showMoreKpis && (
          <div className="grid grid-cols-2 gap-3">
            {SECONDARY_KEYS.map((k) => <KpiCard key={k} kkey={k} />)}
          </div>
        )}
        <button onClick={() => setShowMoreKpis((v) => !v)} data-testid="btn-mais-indicadores"
          className="flex items-center gap-1 text-xs font-semibold text-primary mx-auto">
          {showMoreKpis ? "Ocultar indicadores" : "Ver mais indicadores"}
          <ChevronDown className={`w-4 h-4 transition-transform ${showMoreKpis ? "rotate-180" : ""}`} />
        </button>
      </div>
      <div className="hidden lg:grid grid-cols-3 gap-4">
        {ALL_KEYS.map((k) => <KpiCard key={k} kkey={k} />)}
      </div>

      {/* Fluxo Operacional */}
      <div className="rounded-lg border border-primary/40 bg-card/70 p-4" data-testid="fluxo-operacional">
        <div className="font-display text-base sm:text-lg font-bold uppercase mb-1">Fluxo Operacional</div>
        <div className="text-xs text-muted-foreground mb-3">Toque em uma etapa para ver os pedidos que aguardam ação.</div>

        {/* Desktop: horizontal */}
        <div className="hidden lg:flex items-stretch gap-2 overflow-x-auto pb-1">
          {data.fluxo.map((f, i) => (
            <div key={f.status} className="flex items-center gap-2 shrink-0">
              <button onClick={() => nav(`/pedidos?status=${f.status}${unitSuffix("&")}`)} data-testid={`fluxo-${f.status}`}
                className="min-w-[130px] text-left rounded-md border border-border bg-secondary/40 hover:border-primary/60 hover:bg-secondary transition-colors p-3">
                <div className="text-[10px] uppercase text-muted-foreground leading-tight">{i + 1}. {f.label}</div>
                <div className={`font-display text-3xl font-extrabold ${f.count > 0 ? "text-primary" : "text-muted-foreground"}`}>{f.count}</div>
              </button>
              {i < data.fluxo.length - 1 && <span className="text-muted-foreground">→</span>}
            </div>
          ))}
        </div>

        {/* Mobile: lista vertical compacta, pendências primeiro */}
        <div className="lg:hidden flex flex-col gap-1.5" data-testid="fluxo-mobile">
          {fluxoOrdenado.map((f) => (
            <button key={f.status} onClick={() => nav(`/pedidos?status=${f.status}${unitSuffix("&")}`)} data-testid={`fluxo-m-${f.status}`}
              className="flex items-center gap-3 w-full text-left rounded-md border border-border bg-secondary/40 active:bg-secondary transition-colors px-3 py-2.5">
              <span className={`font-display text-xl font-extrabold w-7 text-center shrink-0 ${f.count > 0 ? "text-primary" : "text-muted-foreground/60"}`}>{f.count}</span>
              <span className={`flex-1 text-sm ${f.count > 0 ? "text-foreground font-medium" : "text-muted-foreground"}`}>{f.label}</span>
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="bg-card/70 p-4 lg:col-span-1 hidden lg:block">
          <div className="font-display text-lg font-bold uppercase mb-3">Pedidos por Status</div>
          <div className="space-y-2">
            {Object.entries(data.por_status).sort((a, b) => b[1] - a[1]).map(([st, n]) => (
              <div key={st} className="flex items-center justify-between text-sm">
                <StatusBadge status={st} />
                <span className="font-mono font-bold">{n}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="bg-card/70 p-4 lg:col-span-2">
          <div className="font-display text-base sm:text-lg font-bold uppercase mb-3">Pedidos Recentes</div>
          <div className="divide-y divide-border">
            {data.pedidos_recentes.map((p) => (
              <div key={p.id} onClick={() => nav(`/pedidos/${p.id}`)} data-testid={`dash-pedido-${p.numero}`}
                className="flex items-center justify-between gap-2 py-2.5 cursor-pointer hover:bg-secondary/40 px-2 -mx-2 rounded">
                <div className="min-w-0">
                  <div className="font-mono font-bold text-sm truncate">{p.numero} {p.tipo === "COMPLEMENTAR" && <span className="text-amber-300 text-xs">(Compl.)</span>}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(p.created_at)}</div>
                </div>
                <StatusBadge status={p.status} />
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
